// generateNoise.js - Utility function for generating different types of audio noise

import { AUDIO_CONFIG } from '../config/constants';
import audioService from '../services/audioService';

/**
 * Returns a per-sample generator function for the requested noise type.
 * Each call returns an independent generator (own filter state), so the
 * two stereo channels can be decorrelated.
 */
function createNoiseGenerator(type) {
  if (type === 'pink') {
    // Paul Kellet's pink noise filter (-3 dB/octave)
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    return () => {
      const white = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + white * 0.0555179;
      b1 = 0.99332 * b1 + white * 0.0750759;
      b2 = 0.96900 * b2 + white * 0.1538520;
      b3 = 0.86650 * b3 + white * 0.3104856;
      b4 = 0.55000 * b4 + white * 0.5329522;
      b5 = -0.7616 * b5 - white * 0.0168980;
      const pink = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
      b6 = white * 0.115926;
      return pink * 0.11; // Scale to stay within [-1, 1]
    };
  }

  if (type === 'brown') {
    // Leaky integrator of white noise (-6 dB/octave)
    let lastOut = 0;
    return () => {
      const white = Math.random() * 2 - 1;
      lastOut = (lastOut + 0.02 * white) / 1.02;
      return lastOut * 3.5; // Scale brown noise which is naturally quieter
    };
  }

  // White noise: independent random values between -1 and 1
  return () => Math.random() * 2 - 1;
}

/**
 * Generates a buffer of noise whose end crossfades into its beginning,
 * so it can loop without an audible seam.
 */
function generateLoopableSamples(type, length, crossfadeLength) {
  const nextSample = createNoiseGenerator(type);

  // Generate a pre-roll of crossfadeLength samples that conceptually
  // precedes the loop start, so the tail can blend into it seamlessly
  const raw = new Float32Array(length + crossfadeLength);
  for (let i = 0; i < raw.length; i++) {
    raw[i] = nextSample();
  }

  const samples = new Float32Array(length);
  samples.set(raw.subarray(crossfadeLength));

  for (let k = 0; k < crossfadeLength; k++) {
    const t = k / crossfadeLength;
    const i = length - crossfadeLength + k;
    samples[i] = samples[i] * (1 - t) + raw[k] * t;
  }

  return samples;
}

/**
 * Generates different types of audio noise (white, pink, or brown)
 *
 * The noise is pre-rendered into a looping stereo AudioBuffer (with
 * decorrelated channels for a wide image), so no per-sample work happens
 * on the main thread during playback.
 *
 * @param {string} type - The type of noise to generate ('white', 'pink', or 'brown')
 * @param {Function} setGainNode - Callback function to set the gain node in the parent component
 * @param {number} initialVolume - Initial volume level (0.0 to 1.0), defaults to 0.5
 * @returns {Object} - Object containing stop function and gainNode reference
 */
export default function generateNoise(type, setGainNode, initialVolume = AUDIO_CONFIG.FADE_IN_VOLUME) {
  const audioCtx = audioService.getContext();

  const length = Math.floor(audioCtx.sampleRate * AUDIO_CONFIG.NOISE_LOOP_SECONDS);
  const crossfadeLength = Math.floor(audioCtx.sampleRate * AUDIO_CONFIG.NOISE_LOOP_CROSSFADE_SECONDS);

  const buffer = audioCtx.createBuffer(2, length, audioCtx.sampleRate);
  for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
    buffer.getChannelData(channel).set(generateLoopableSamples(type, length, crossfadeLength));
  }

  const sourceNode = audioCtx.createBufferSource();
  sourceNode.buffer = buffer;
  sourceNode.loop = true;

  const gainNode = audioCtx.createGain();
  // Exponential ramps cannot start from 0, so fade in from the minimum gain
  gainNode.gain.value = AUDIO_CONFIG.MIN_GAIN_VALUE;

  sourceNode.connect(gainNode);
  gainNode.connect(audioCtx.destination);
  sourceNode.start();

  setGainNode(gainNode);

  // Ramp up the volume to prevent clicks
  const targetVolume = Math.max(initialVolume, AUDIO_CONFIG.MIN_GAIN_VALUE);
  gainNode.gain.setValueAtTime(AUDIO_CONFIG.MIN_GAIN_VALUE, audioCtx.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(targetVolume, audioCtx.currentTime + 0.1);

  return {
    stop: () => {
      // Fade out to prevent clicks
      const now = audioCtx.currentTime;
      const stopTime = now + 0.1;
      gainNode.gain.cancelScheduledValues(now);
      gainNode.gain.setValueAtTime(gainNode.gain.value, now);
      gainNode.gain.exponentialRampToValueAtTime(AUDIO_CONFIG.MIN_GAIN_VALUE, stopTime);

      sourceNode.stop(stopTime);

      // Disconnect nodes after the fade out; the shared context stays open
      setTimeout(() => {
        sourceNode.disconnect();
        gainNode.disconnect();
      }, 150);
    },
    gainNode: gainNode
  };
}
